import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/common/DashboardLayout';
import StatusBadge from '../components/common/StatusBadge';
import Pagination from '../components/common/Pagination';
import { agencyAPI, adoptionAPI, monitoringAPI } from '../services/api';
import MonitoringMapCard, { PhotoCarousel } from '../components/common/MonitoringMapCard';
import PhotoLightbox from '../components/common/PhotoLightbox';
import ProjectSpeciesSection from '../components/common/ProjectSpeciesSection';
import SpeciesReport from './SpeciesReport';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

/* Helper: fly map to a zone's bounds (polygon or photo markers) */
function ZoneFlyTo({ zones, entries, selectedZoneId }) {
  const map = useMap();
  React.useEffect(() => {
    if (!selectedZoneId) return;
    const zone = (zones || []).find(z => z.id === selectedZoneId);
    if (!zone) return;
    const pts = [];
    if (zone.boundary_polygon) zone.boundary_polygon.forEach(p => pts.push([p.lat, p.lng]));
    if (zone.center_latitude && zone.center_longitude) pts.push([zone.center_latitude, zone.center_longitude]);
    // Also gather photo GPS points from entries belonging to this zone
    (entries || []).filter(e => e.zone_id === selectedZoneId).forEach(e => {
      if (e.latitude && e.longitude) pts.push([e.latitude, e.longitude]);
      (e.photos || []).forEach(p => { if (p.latitude && p.longitude) pts.push([p.latitude, p.longitude]); });
    });
    if (pts.length > 0) {
      const b = L.latLngBounds(pts).pad(0.15);
      map.flyToBounds(b, { duration: 0.8, maxZoom: 18 });
    }
  }, [selectedZoneId, zones, entries, map]);
  return null;
}

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const navItems = [
  { path: '', label: 'Dashboard', icon: '\uD83D\uDCCA' },
  { section: 'Opportunities' },
  { path: '/opportunities', label: 'Browse Opportunities', icon: '\uD83D\uDD0D' },
  { path: '/applications', label: 'My Applications', icon: '\uD83D\uDCDD' },
  { section: 'Projects' },
  { path: '/projects', label: 'Active Projects', icon: '\uD83C\uDF31' },
  { path: '/reports', label: 'Milestone Reports', icon: '\uD83D\uDCCB' },
  { section: 'Adoption Management' },
  { path: '/adoption-sites', label: 'Managed Adoption Sites', icon: '\uD83C\uDFDE\uFE0F' },
  { path: '/adoption-reports', label: 'Adoption Reports', icon: '\uD83D\uDCDD' },
  { section: 'Team' },
  { path: '/field-supervisors', label: 'Field Supervisors', icon: '\uD83E\uDDD1\u200D\uD83C\uDF3F' },
  { section: 'Monitoring' },
  { path: '/monitoring-progress', label: 'Monitoring Progress', icon: '\uD83D\uDCCA' },
  { path: '/zones', label: 'Monitoring Zones', icon: '\uD83D\uDCCD' },
  { path: '/group-monitoring', label: 'Group Monitoring', icon: '\uD83C\uDF33' },
  { path: '/species-report', label: 'Species Report', icon: '\uD83C\uDF3B' },
  { section: 'Community' },
  { path: '/ideas', label: 'Collaboration Ideas', icon: '\uD83D\uDCA1' },
  { path: '/blog', label: 'My Blog Posts', icon: '\u270D\uFE0F' },
];

/* ──────────────────────────────────────────────
   Overview (default route) - Modern Chart-Rich Dashboard
   ────────────────────────────────────────────── */
function Overview() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const res = await agencyAPI.getDashboard();
        setDashboard(res.data);
      } catch {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (!dashboard) return <div className="empty-state">Unable to load dashboard data.</div>;

  const {
    total_allocated_plants = 0,
    total_reports = 0,
    report_status = {},
    report_types = {},
    project_progress = [],
    total_trees_monitored = 0,
    monitoring_avg_survival = 0,
  } = dashboard;

  const approvedReports = report_status.approved || 0;
  const reportsProgress = total_reports > 0 ? Math.round((approvedReports / total_reports) * 100) : 0;
  const fmt = (n) => (n || 0).toLocaleString();

  const card = { background: '#fff', borderRadius: 16, padding: '22px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' };

  return (
    <div>
      {/* ── KPI Strip ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { icon: '\uD83C\uDF33', value: fmt(total_trees_monitored), label: 'Trees Monitored', sub: `${total_allocated_plants > 0 ? ((total_trees_monitored / total_allocated_plants) * 100).toFixed(1) : 0}% of ${fmt(total_allocated_plants)} target`, color: '#16a34a', path: '/agency/projects' },
          { icon: '\uD83D\uDCC8', value: `${monitoring_avg_survival}%`, label: 'Survival Rate', sub: `Across ${total_reports} reports`, color: '#0891b2', path: '/agency/reports' },
          { icon: '\uD83D\uDCCB', value: `${approvedReports}/${total_reports}`, label: 'Reports Approved', sub: `${reportsProgress}% approval rate`, color: '#059669', path: '/agency/reports' },
        ].map((kpi, i) => (
          <div key={i} onClick={() => navigate(kpi.path)} style={{ ...card, borderLeft: `4px solid ${kpi.color}`, cursor: 'pointer', transition: 'transform 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.06)'; }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{kpi.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.1 }}>{kpi.value}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4 }}>{kpi.label}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Monitoring & GPS Map (+ Monitoring History calendar) */}
      <MonitoringMapCard fetchSummary={agencyAPI.getMonitoringSummary} />

      {/* ── Per-Project Progress Cards ── */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 18, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>Project-wise Progress</div>
        {project_progress.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>No active projects yet</div>
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            {project_progress.map((p, i) => {
              const pCount = p.monitored || p.planted || 0;
              const pct = p.allocated > 0 ? Math.round((pCount / p.allocated) * 100) : 0;
              const barColor = pct >= 75 ? '#16a34a' : pct >= 40 ? '#f59e0b' : '#ef4444';
              return (
                <div key={p.id || i} onClick={() => navigate(`/agency/projects/${p.id}`)} style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 20, padding: '18px 20px', border: '1px solid #f0f0f0', borderRadius: 14, cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s', background: '#fafffe' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(22,163,106,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#f0f0f0'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', marginBottom: 10 }}>{p.name}</div>
                    {/* Plantation bar */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#64748b' }}>Plantation</span>
                        <span style={{ fontWeight: 700, color: barColor }}>{fmt(pCount)} / {fmt(p.allocated)} trees ({pct}%)</span>
                      </div>
                      <div style={{ height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: barColor, borderRadius: 5, transition: 'width 0.8s' }} />
                      </div>
                    </div>
                  </div>
                  {/* Circular progress */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ position: 'relative', width: 80, height: 80 }}>
                      <svg width="80" height="80" viewBox="0 0 80 80">
                        <circle cx="40" cy="40" r="34" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                        <circle cx="40" cy="40" r="34" fill="none" stroke={barColor} strokeWidth="8" strokeLinecap="round"
                          strokeDasharray={`${(pct / 100) * 213.6} 213.6`} transform="rotate(-90 40 40)" style={{ transition: 'stroke-dasharray 0.8s' }} />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: barColor }}>{pct}%</div>
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Planted</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Report & Milestone Summary ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>Reports & Milestones</div>
          {/* Milestone pipeline */}
          <div style={{ display: 'grid', gap: 14 }}>
            {[
              { key: 'land_preparation', label: 'Land Preparation', icon: '\uD83D\uDEA7' },
              { key: 'plantation_progress', label: 'Plantation Progress', icon: '\uD83C\uDF31' },
              { key: 'plantation_done', label: 'Plantation Done', icon: '\u2705' },
              { key: 'survival_reporting', label: 'Survival Reporting', icon: '\uD83D\uDCC8' },
            ].map((m) => {
              const count = report_types[m.key] || 0;
              return (
                <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: count > 0 ? '#f0fdf4' : '#fafafa', borderRadius: 10, border: count > 0 ? '1px solid #bbf7d0' : '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 22 }}>{m.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {count > 0 ? `${count} report${count > 1 ? 's' : ''}` : 'No reports'}
                    </div>
                  </div>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: count > 0 ? '#16a34a' : '#e5e7eb', color: count > 0 ? '#fff' : '#9ca3af', fontWeight: 800, fontSize: 14 }}>
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Browse Opportunities', icon: '\uD83D\uDD0D', bg: '#16a34a', path: '/agency/opportunities' },
          { label: 'Submit Report', icon: '\uD83D\uDCCB', bg: '#0d9488', path: '/agency/reports' },
          { label: 'View Projects', icon: '\uD83C\uDF31', bg: '#059669', path: '/agency/projects' },
          { label: 'Group Monitoring', icon: '\uD83C\uDF33', bg: '#d97706', path: '/agency/group-monitoring' },
        ].map((a, i) => (
          <button key={i} onClick={() => navigate(a.path)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: a.bg, color: '#fff', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'opacity 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
            <span style={{ fontSize: 20 }}>{a.icon}</span> {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Opportunities – browse published targets
   ────────────────────────────────────────────── */
function Opportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ proposal: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await agencyAPI.getOpportunities({ page: p, per_page: 12 });
      setOpportunities(res.data.opportunities || res.data.targets || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  const openApply = (id) => {
    setSelectedId(id);
    setForm({ proposal: '' });
    setShowModal(true);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!form.proposal.trim()) {
      toast.warn('Please write a proposal');
      return;
    }
    setSubmitting(true);
    try {
      await agencyAPI.applyOpportunity(selectedId, {
        proposal: form.proposal,
      });
      toast.success('Application submitted successfully!');
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading opportunities...</div>;

  return (
    <div>
      <h3>Browse Opportunities</h3>

      {opportunities.length === 0 ? (
        <div className="empty-state">No opportunities available at this time.</div>
      ) : (
        <div className="card-grid">
          {opportunities.map((opp) => (
            <div className="card" key={opp.id}>
              <div className="card-header">
                <h4>{opp.title}</h4>
              </div>
              <div className="card-body">
                <p><strong>CSR Company:</strong> {opp.csr_company_name || opp.csr_name || '-'}</p>
                <p><strong>Total Plants:</strong> {opp.total_plants?.toLocaleString() || '-'}</p>
                <p><strong>Location:</strong> {[opp.city, opp.state].filter(Boolean).join(', ') || '-'}</p>
                <p><strong>Posted:</strong> {opp.created_at ? new Date(opp.created_at).toLocaleDateString() : '-'}</p>
              </div>
              <div className="card-footer">
                <button className="btn btn-primary" onClick={() => openApply(opp.id)}>
                  Apply
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} pages={pages} onPageChange={setPage} />

      {/* Apply Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Apply for Opportunity</h4>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleApply}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Proposal</label>
                  <textarea
                    rows={5}
                    value={form.proposal}
                    onChange={(e) => setForm({ ...form, proposal: e.target.value })}
                    placeholder="Describe your approach, experience, and how you plan to execute this project..."
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Applications – list of submitted applications
   ────────────────────────────────────────────── */
function Applications() {
  const [applications, setApplications] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await agencyAPI.getApplications({ page: p, per_page: 15 });
      setApplications(res.data.applications || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  if (loading) return <div className="loading">Loading applications...</div>;

  return (
    <div>
      <h3>My Applications</h3>

      {applications.length === 0 ? (
        <div className="empty-state">You have not submitted any applications yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Target / Opportunity</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id}>
                  <td>{app.target_title || app.opportunity_title || `Target #${app.target_id}`}</td>
                  <td><StatusBadge status={app.status} /></td>
                  <td>{app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} pages={pages} onPageChange={setPage} />
    </div>
  );
}

/* ──────────────────────────────────────────────
   Projects – active/allocated projects
   ────────────────────────────────────────────── */
function Projects() {
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await agencyAPI.getProjects({ page: p, per_page: 12 });
      setProjects(res.data.projects || res.data.allocations || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  if (loading) return <div className="loading">Loading projects...</div>;

  return (
    <div>
      <h3>Active Projects</h3>

      {projects.length === 0 ? (
        <div className="empty-state">No active projects. Apply to opportunities to get started!</div>
      ) : (
        <div className="card-grid">
          {projects.map((proj) => (
            <div className="card" key={proj.id}>
              <div className="card-header">
                <h4>{proj.target?.title || proj.target_title || proj.title || `Project #${proj.id}`}</h4>
              </div>
              <div className="card-body">
                <p><strong>Allocated Plants:</strong> {proj.allocated_plants?.toLocaleString() || proj.plant_count?.toLocaleString() || '-'}</p>
                <p>
                  <strong>Status:</strong>{' '}
                  <StatusBadge status={proj.status} />
                </p>
              </div>
              <div className="card-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/agency/projects/${proj.id}`)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} pages={pages} onPageChange={setPage} />
    </div>
  );
}

/* ──────────────────────────────────────────────
   Project Detail – view allocated project details
   ────────────────────────────────────────────── */
function ProjectDetail() {
  const HISTORY_PER_PAGE = 5;
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxEntry, setLightboxEntry] = useState('');

  useEffect(() => {
    agencyAPI.getProject(id)
      .then(res => setProject(res.data.project || res.data))
      .catch(() => toast.error('Failed to load project details'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!project) return <div className="empty-state">Project not found.</div>;

  const target = project.target || {};
  const milestoneLabels = { land_preparation: 'Land Preparation', plantation_progress: 'Plantation Progress', plantation_done: 'Plantation Done', survival_reporting: 'Survival Reporting' };
  const statusColors = { pending: '#ff9800', approved: '#4caf50', disbursed: '#2196f3', submitted: '#ff9800', rejected: '#ef5350', under_review: '#ff9800' };

  return (
    <div>
      <button onClick={() => navigate('/agency/projects')}
        style={{ background: 'none', border: 'none', color: '#2e7d32', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 20, padding: 0 }}>
        &larr; Back to Projects
      </button>

      {/* Project Overview Card */}
      <div className="card" style={{ padding: 28, marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', color: '#1b5e20' }}>{target.title || project.target_title || project.target?.title || `Project #${project.id}`}</h2>
        <div style={{ fontSize: 14, color: '#888', marginBottom: 20 }}>
          {target.location || [target.city, target.state].filter(Boolean).join(', ')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <div style={{ background: '#e8f5e9', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Allocated Trees</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1b5e20' }}>{(project.allocated_plants || 0).toLocaleString()}</div>
          </div>
          <div style={{ background: '#fff3e0', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Status</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#e65100' }}><StatusBadge status={project.status} /></div>
          </div>
          <div style={{ background: '#f3e5f5', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>CSR</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#6a1b9a' }}>{project.csr_name || '-'}</div>
          </div>
        </div>
      </div>

      {/* Milestones */}
      {project.milestones && project.milestones.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Milestones</h3>
          <table className="data-table">
            <thead>
              <tr><th>Milestone</th><th>Due Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              {project.milestones.map(m => (
                <tr key={m.id}>
                  <td>{milestoneLabels[m.milestone_type] || m.milestone_type || m.title}</td>
                  <td>{m.due_date ? new Date(m.due_date).toLocaleDateString() : '-'}</td>
                  <td><StatusBadge status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reports */}
      {project.reports && project.reports.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Submitted Reports</h3>
          <table className="data-table">
            <thead>
              <tr><th>Type</th><th>Plant Count</th><th>Survival Rate</th><th>Status</th><th>Date</th></tr>
            </thead>
            <tbody>
              {project.reports.map(r => (
                <tr key={r.id}>
                  <td>{milestoneLabels[r.report_type] || r.report_type}</td>
                  <td>{r.plant_count?.toLocaleString() || '-'}</td>
                  <td>{r.survival_rate != null ? `${r.survival_rate}%` : '-'}</td>
                  <td>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[r.status] || '#666', background: (statusColors[r.status] || '#666') + '18' }}>
                      {r.status}
                    </span>
                  </td>
                  <td>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Plant Species (read-only for Agency) */}
      <ProjectSpeciesSection targetId={project.target_id || project.target?.id || project.id} adminMode={false} />

      {/* GPS Map — show zone polygons and photo markers */}
      {(() => {
        const zones = project.monitoring_zones || [];
        const entries = project.monitoring_entries || [];
        // Build zone color lookup
        const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
        const zoneColorMap = {};
        zones.forEach((z, i) => { zoneColorMap[z.id] = zoneColors[i % zoneColors.length]; });
        // Collect all GPS points for map bounds
        const allPoints = [];
        zones.forEach(z => {
          if (z.boundary_polygon) z.boundary_polygon.forEach(p => allPoints.push([p.lat, p.lng]));
          if (z.center_latitude && z.center_longitude) allPoints.push([z.center_latitude, z.center_longitude]);
        });
        entries.forEach(e => {
          if (e.latitude && e.longitude) allPoints.push([e.latitude, e.longitude]);
          (e.photos || []).forEach(p => { if (p.latitude && p.longitude) allPoints.push([p.latitude, p.longitude]); });
        });
        if (allPoints.length === 0) return null;
        const bounds = L.latLngBounds(allPoints);
        return (
          <div className="card" style={{ padding: 28, marginBottom: 24 }}>
            <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring & GPS Mapping</h3>
            <div style={{ height: 450, borderRadius: 12, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <MapContainer bounds={bounds.pad(0.1)} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Esri" />
                <ZoneFlyTo zones={zones} entries={entries} selectedZoneId={selectedZoneId} />
                {zones.map((z) => z.boundary_polygon && (
                  <Polygon key={z.id} positions={z.boundary_polygon.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: zoneColorMap[z.id], weight: selectedZoneId === z.id ? 5 : 3, fillOpacity: selectedZoneId === z.id ? 0.35 : 0.2 }}>
                    <Popup><strong>{z.name}</strong><br />{z.tree_count?.toLocaleString()} trees{z.area_sqm ? ` | ${Number(z.area_sqm).toLocaleString()} sq m` : ''}</Popup>
                  </Polygon>
                ))}
                {(() => {
                  const groupMap = new Map();
                  entries.forEach(e => {
                    (e.photos || []).forEach(p => {
                      if (!(p.latitude && p.longitude)) return;
                      const key = e.zone_id ? `zone-${e.zone_id}` : `entry-${e.id}`;
                      if (!groupMap.has(key)) {
                        groupMap.set(key, {
                          zoneId: e.zone_id || null,
                          label: e.zone_name || 'Monitoring Entry',
                          color: zoneColorMap[e.zone_id] || '#e65100',
                          photos: [],
                        });
                      }
                      groupMap.get(key).photos.push({
                        id: p.id,
                        url: getPhotoUrl(p.photo_url),
                        type: p.photo_type,
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                        zoneName: e.zone_name,
                        entryDate: e.monitoring_date,
                      });
                    });
                  });
                  const groups = [];
                  groupMap.forEach((g, key) => {
                    if (g.photos.length === 0) return;
                    const cLat = g.photos.reduce((s, p) => s + p.lat, 0) / g.photos.length;
                    const cLng = g.photos.reduce((s, p) => s + p.lng, 0) / g.photos.length;
                    groups.push({ key, centroidLat: cLat, centroidLng: cLng, ...g });
                  });
                  return groups.map(g => {
                    const isSelected = selectedZoneId && g.zoneId === selectedZoneId;
                    const size = isSelected ? 34 : 26;
                    const count = g.photos.length;
                    return (
                      <Marker key={g.key} position={[g.centroidLat, g.centroidLng]}
                        icon={L.divIcon({
                          className: '',
                          html: `<div style="background:${g.color};color:#fff;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 0 6px rgba(0,0,0,0.5);font-size:${size >= 30 ? 13 : 11}px;font-weight:700;${isSelected ? 'animation:pulse 1s infinite;' : ''}">${count}</div>`,
                          iconSize: [size, size],
                          iconAnchor: [size/2, size/2],
                        })}>
                        <Popup maxWidth={320} minWidth={280}>
                          <PhotoCarousel
                            photos={g.photos}
                            label={g.label}
                            centroidLat={g.centroidLat}
                            centroidLng={g.centroidLng}
                          />
                        </Popup>
                      </Marker>
                    );
                  });
                })()}
              </MapContainer>
            </div>
            {/* Clickable zone legend */}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
              <span onClick={() => setSelectedZoneId(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: !selectedZoneId ? '#e8f5e9' : '#fff', border: !selectedZoneId ? '2px solid #4caf50' : '1px solid #e0e0e0', fontWeight: !selectedZoneId ? 700 : 400, transition: 'all 0.2s' }}>
                🗺️ All Zones
              </span>
              {zones.map((z) => (
                <span key={z.id} onClick={() => setSelectedZoneId(selectedZoneId === z.id ? null : z.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: selectedZoneId === z.id ? zoneColorMap[z.id] + '22' : '#fff', border: selectedZoneId === z.id ? `2px solid ${zoneColorMap[z.id]}` : '1px solid #e0e0e0', fontWeight: selectedZoneId === z.id ? 700 : 400, transition: 'all 0.2s' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: zoneColorMap[z.id], display: 'inline-block' }} />
                  {z.name} ({(z.tree_count || 0).toLocaleString()} trees)
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Monitoring Zones cards — clickable to zoom map */}
      {project.monitoring_zones && project.monitoring_zones.length > 0 && (() => {
        const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
        return (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring Zones <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>— click a zone to see it on the map</span></h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {project.monitoring_zones.map((z, i) => {
              const isActive = selectedZoneId === z.id;
              const zColor = zoneColors[i % zoneColors.length];
              return (
              <div key={z.id} onClick={() => { setSelectedZoneId(isActive ? null : z.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{ border: isActive ? `2px solid ${zColor}` : '1px solid #e0e0e0', borderRadius: 12, padding: 16, background: isActive ? zColor + '0a' : '#fafafa', cursor: 'pointer', transition: 'all 0.25s', transform: isActive ? 'scale(1.02)' : 'scale(1)', boxShadow: isActive ? `0 4px 16px ${zColor}33` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, background: zColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#1b5e20', fontSize: 15 }}>{z.name}</span>
                  {isActive && <span style={{ marginLeft: 'auto', fontSize: 11, background: zColor, color: '#fff', padding: '2px 8px', borderRadius: 10 }}>Viewing on map</span>}
                </div>
                {z.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{z.description}</div>}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>Trees: <strong>{(z.tree_count || 0).toLocaleString()}</strong></span>
                  {z.area_sqm && <span>Area: <strong>{Number(z.area_sqm).toLocaleString()}</strong> sq m</span>}
                  <span style={{ textTransform: 'capitalize', color: '#888' }}>{z.zone_type?.replace('_', ' ')}</span>
                </div>
                {z.boundary_polygon ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#4caf50', fontWeight: 600 }}>📍 GPS Boundary Mapped ({z.boundary_polygon.length} points)</div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>GPS boundary not mapped yet</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>{z.entries_count || 0} monitoring {(z.entries_count || 0) === 1 ? 'entry' : 'entries'}</div>
              </div>
            );})}
          </div>
        </div>
      );})()}

      {/* Monitoring Entries with Photos */}
      {project.monitoring_entries && project.monitoring_entries.length > 0 && (() => {
        const allEntries = project.monitoring_entries;
        const historyPages = Math.max(1, Math.ceil(allEntries.length / HISTORY_PER_PAGE));
        const currentPage = Math.min(historyPage, historyPages);
        const pagedEntries = allEntries.slice((currentPage - 1) * HISTORY_PER_PAGE, currentPage * HISTORY_PER_PAGE);
        return (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: '#1b5e20' }}>Monitoring History</h3>
            <span style={{ fontSize: 12, color: '#888' }}>
              Showing {(currentPage - 1) * HISTORY_PER_PAGE + 1}&ndash;{Math.min(currentPage * HISTORY_PER_PAGE, allEntries.length)} of {allEntries.length}
            </span>
          </div>
          {pagedEntries.map(e => {
            const healthColors = { excellent: '#1b5e20', good: '#4caf50', fair: '#ff9800', poor: '#f44336', critical: '#b71c1c' };
            const photos = e.photos || [];
            return (
              <div key={e.id} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 16, marginBottom: 16, background: '#fafffe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong style={{ fontSize: 14 }}>{e.zone_name || 'Unknown Zone'}</strong>
                    <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{e.monitoring_date ? new Date(e.monitoring_date).toLocaleDateString() : ''}</span>
                    {e.agency_name && <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>by {e.agency_name}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: healthColors[e.overall_health] || '#666', background: (healthColors[e.overall_health] || '#666') + '18', textTransform: 'capitalize' }}>{e.overall_health}</span>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[e.status] || '#666', background: (statusColors[e.status] || '#666') + '18' }}>{e.status}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>Trees: <strong>{(e.total_trees_monitored || 0).toLocaleString()}</strong></span>
                  <span>Healthy: <strong style={{ color: '#4caf50' }}>{e.trees_healthy || 0}</strong></span>
                  <span>Attention: <strong style={{ color: '#ff9800' }}>{e.trees_needs_attention || 0}</strong></span>
                  <span>Critical: <strong style={{ color: '#f44336' }}>{e.trees_critical || 0}</strong></span>
                  <span>Dead: <strong style={{ color: '#999' }}>{e.trees_dead || 0}</strong></span>
                  {e.survival_rate != null && <span>Survival: <strong>{e.survival_rate}%</strong></span>}
                </div>
                {e.notes && <div style={{ marginTop: 8, fontSize: 13, color: '#555', fontStyle: 'italic' }}>{e.notes}</div>}
                {e.latitude && e.longitude && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#1976d2' }}>
                    📍 Entry GPS: {Number(e.latitude).toFixed(6)}, {Number(e.longitude).toFixed(6)}
                  </div>
                )}
                {/* Photos */}
                {photos.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>Photos ({photos.length})</div>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                      {photos.map((p, pIdx) => (
                        <div key={p.id} onClick={() => { setLightboxPhotos(photos); setLightboxIndex(pIdx); setLightboxEntry(e.zone_name || ''); }}
                          style={{ flexShrink: 0, textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ position: 'relative', width: 100, height: 75 }}>
                            <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type}
                              style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, border: '2px solid #e0e0e0' }}
                              onError={ev => { ev.target.style.display = 'none'; }} />
                            {p.latitude && p.longitude && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 8, padding: '2px 3px', borderRadius: '0 0 6px 6px', lineHeight: 1.2, textAlign: 'center' }}>
                                {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2, textTransform: 'capitalize' }}>{p.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {historyPages > 1 && (
            <div style={{ marginTop: 12, marginBottom: 8 }}>
              <Pagination page={currentPage} pages={historyPages} onPageChange={setHistoryPage} />
            </div>
          )}
          {/* Summary stats (planted count from project) */}
          {(() => {
            const entries = project.monitoring_entries;
            const totalMonitored = project.plants_reported || 0;
            const _approved = entries.filter(e => e.status === 'approved');
            const _treesSum = _approved.reduce((s, e) => s + (e.total_trees_monitored || 0), 0);
            const _wt = _approved.reduce((s, e) => s + (e.total_trees_monitored || 0) * (e.survival_rate || 0), 0);
            const avgSurvival = _treesSum > 0 ? (_wt / _treesSum).toFixed(1) : null;
            const totalPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);
            return (
              <div style={{ display: 'flex', gap: 24, marginTop: 8, padding: '12px 16px', background: '#f5f5f5', borderRadius: 10, fontSize: 13, flexWrap: 'wrap' }}>
                <span>Total Visits: <strong>{entries.length}</strong></span>
                <span>Trees Monitored: <strong>{totalMonitored.toLocaleString()}</strong></span>
                {avgSurvival && <span>Avg Survival: <strong>{avgSurvival}%</strong></span>}
                <span>Photos: <strong>{totalPhotos}</strong></span>
              </div>
            );
          })()}
        </div>
        );
      })()}

      {/* No data states */}
      {(!project.milestones || project.milestones.length === 0) && (!project.reports || project.reports.length === 0) && (!project.monitoring_entries || project.monitoring_entries.length === 0) && (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: '#888' }}>
          No milestones, reports, or monitoring data yet for this project.
        </div>
      )}
      {lightboxPhotos && <PhotoLightbox photos={lightboxPhotos} initialIndex={lightboxIndex} entryInfo={lightboxEntry} onClose={() => setLightboxPhotos(null)} />}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Reports – milestone reporting
   ────────────────────────────────────────────── */
function Reports() {
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [form, setForm] = useState({
    report_type: 'land_preparation',
    description: '',
    plant_count: '',
    survival_rate: '',
    reporting_mode: 'individual',
    allocation_id: '',
    zone_id: '',
    sponsorship_id: '',
  });

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await agencyAPI.getReports({ page: p, per_page: 15 });
      setReports(res.data.reports || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  // Load projects when modal opens
  useEffect(() => {
    if (showModal && projects.length === 0) {
      agencyAPI.getProjects({ per_page: 100 })
        .then(res => {
          const items = res.data.items || res.data.projects || [];
          setProjects(items);
        })
        .catch(() => {});
    }
  }, [showModal, projects.length]);

  // Load zones when project changes
  useEffect(() => {
    if (!form.allocation_id) { setZones([]); return; }
    const proj = projects.find(p => String(p.id) === String(form.allocation_id));
    const targetId = proj?.target_id || proj?.target?.id;
    if (!targetId) { setZones([]); return; }
    setLoadingZones(true);
    setForm(f => ({ ...f, zone_id: '' }));
    monitoringAPI.getZones({ target_id: targetId, per_page: 100 })
      .then(res => {
        setZones(res.data.items || []);
      })
      .catch(() => setZones([]))
      .finally(() => setLoadingZones(false));
  }, [form.allocation_id]); // eslint-disable-line

  const resetForm = () => {
    setForm({
      report_type: 'land_preparation',
      description: '',
      plant_count: '',
      survival_rate: '',
      reporting_mode: 'individual',
      allocation_id: '',
      zone_id: '',
      sponsorship_id: '',
    });
    setZones([]);
    setPdfFile(null);
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!form.allocation_id) {
      toast.warn('Please select a project');
      return;
    }
    if (!form.description.trim()) {
      toast.warn('Please enter a description');
      return;
    }
    // Validate PDF if selected
    if (pdfFile) {
      if (!pdfFile.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Only PDF files are allowed');
        return;
      }
      if (pdfFile.size > 25 * 1024 * 1024) {
        toast.error('File size exceeds 25 MB limit');
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        report_type: form.report_type,
        description: form.description,
        plant_count: form.plant_count ? Number(form.plant_count) : undefined,
        reporting_mode: form.reporting_mode,
      };
      if (form.report_type === 'survival_reporting' && form.survival_rate) {
        payload.survival_rate = Number(form.survival_rate);
      }
      if (form.allocation_id) {
        payload.allocation_id = Number(form.allocation_id);
      }
      if (form.zone_id) {
        payload.zone_id = Number(form.zone_id);
      }
      if (form.sponsorship_id) {
        payload.sponsorship_id = Number(form.sponsorship_id);
      }
      const res = await agencyAPI.submitReport(payload);
      const reportId = res.data?.report?.id;

      // Upload PDF document if selected
      if (pdfFile && reportId) {
        const fd = new FormData();
        fd.append('document', pdfFile);
        try {
          await agencyAPI.uploadReportDocument(reportId, fd);
        } catch (docErr) {
          toast.warn('Report created but PDF upload failed: ' + (docErr.response?.data?.error || 'Unknown error'));
        }
      }

      toast.success('Report submitted successfully!');
      setShowModal(false);
      resetForm();
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  // Get project name for display
  const getProjectName = (allocId) => {
    const proj = projects.find(p => String(p.id) === String(allocId));
    if (!proj) return '';
    const t = proj.target || {};
    return t.title || '';
  };

  if (loading) return <div className="loading">Loading reports...</div>;

  return (
    <div>
      <div className="page-header">
        <h3>Milestone Reports</h3>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          Submit Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="empty-state">No reports submitted yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Report Type</th>
                <th>Description</th>
                <th>Plant Count</th>
                <th>Document</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td style={{ fontWeight: 500, maxWidth: 160 }}>{report.target_title || report.allocation_target || '-'}</td>
                  <td>{report.report_type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '-'}</td>
                  <td className="text-truncate" style={{ maxWidth: 200 }}>{report.description || '-'}</td>
                  <td>{report.plant_count?.toLocaleString() || '-'}</td>
                  <td>{report.report_document
                    ? <a href={report.report_document} target="_blank" rel="noopener noreferrer" style={{ color: '#2e7d32', fontWeight: 500, fontSize: 12 }}>📄 View PDF</a>
                    : <span style={{ color: '#999', fontSize: 12 }}>—</span>
                  }</td>
                  <td><StatusBadge status={report.status} /></td>
                  <td>{report.created_at ? new Date(report.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} pages={pages} onPageChange={setPage} />

      {/* Submit Report Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Submit Milestone Report</h4>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitReport}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Project *</label>
                  <select
                    value={form.allocation_id}
                    onChange={(e) => setForm({ ...form, allocation_id: e.target.value })}
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((p) => {
                      const t = p.target || {};
                      return (
                        <option key={p.id} value={p.id}>
                          {t.title || 'Untitled'} — {t.csr_name || ''} ({(p.allocated_plants || 0).toLocaleString()} trees)
                        </option>
                      );
                    })}
                  </select>
                </div>

                {form.allocation_id && (
                  <div className="form-group">
                    <label>Zone {loadingZones && <span style={{ fontSize: 11, color: '#999' }}>(loading...)</span>}</label>
                    <select
                      value={form.zone_id}
                      onChange={(e) => setForm({ ...form, zone_id: e.target.value })}
                      disabled={loadingZones}
                    >
                      <option value="">All Zones / No specific zone</option>
                      {zones.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.name} ({(z.tree_count || 0).toLocaleString()} trees)
                        </option>
                      ))}
                    </select>
                    {!loadingZones && zones.length === 0 && (
                      <small style={{ color: '#999', fontSize: 11 }}>No zones defined for this project yet</small>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>Report Type *</label>
                  <select
                    value={form.report_type}
                    onChange={(e) => setForm({ ...form, report_type: e.target.value })}
                    required
                  >
                    <option value="land_preparation">Land Preparation</option>
                    <option value="plantation_progress">Plantation Progress</option>
                    <option value="plantation_done">Plantation Done</option>
                    <option value="survival_reporting">Survival Reporting</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Describe the milestone progress..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Plant Count</label>
                  <input
                    type="number"
                    min="0"
                    value={form.plant_count}
                    onChange={(e) => setForm({ ...form, plant_count: e.target.value })}
                    placeholder="Number of plants"
                  />
                </div>

                {form.report_type === 'survival_reporting' && (
                  <div className="form-group">
                    <label>Survival Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.survival_rate}
                      onChange={(e) => setForm({ ...form, survival_rate: e.target.value })}
                      placeholder="e.g. 85.5"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Reporting Mode</label>
                  <select
                    value={form.reporting_mode}
                    onChange={(e) => setForm({ ...form, reporting_mode: e.target.value })}
                  >
                    <option value="individual">Individual</option>
                    <option value="group">Group</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Upload Report (PDF only, max 25 MB)</label>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => {
                      const f = e.target.files[0];
                      if (f) {
                        if (!f.name.toLowerCase().endsWith('.pdf')) {
                          toast.error('Only PDF files are allowed');
                          e.target.value = '';
                          setPdfFile(null);
                          return;
                        }
                        if (f.size > 25 * 1024 * 1024) {
                          toast.error('File size exceeds 25 MB limit');
                          e.target.value = '';
                          setPdfFile(null);
                          return;
                        }
                        setPdfFile(f);
                      } else {
                        setPdfFile(null);
                      }
                    }}
                    style={{ padding: '8px 0' }}
                  />
                  {pdfFile && (
                    <small style={{ color: '#2e7d32', fontSize: 12 }}>
                      ✓ {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(1)} MB)
                    </small>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Managed Adoption Sites – sites assigned to this agency
   ────────────────────────────────────────────── */
function ManagedAdoptionSites() {
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await adoptionAPI.getAgencySites({ page: p, per_page: 12 });
      setSites(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load managed sites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  if (loading) return <div className="loading">Loading managed sites...</div>;

  return (
    <div>
      <h3>Managed Adoption Sites</h3>

      {sites.length === 0 ? (
        <div className="empty-state">No adoption sites are currently assigned to your agency.</div>
      ) : (
        <>
          <div className="card-grid">
            {sites.map((site) => (
              <div className="card" key={site.id} style={{ padding: 20 }}>
                <h4 style={{ margin: '0 0 8px' }}>{site.name}</h4>
                <StatusBadge status={site.status} />
                <div style={{ fontSize: 14, color: '#555', lineHeight: 1.8, marginTop: 12 }}>
                  <div><strong>Type:</strong> <span style={{ textTransform: 'capitalize' }}>{site.site_type?.replace(/_/g, ' ')}</span></div>
                  <div><strong>Location:</strong> {[site.city, site.state].filter(Boolean).join(', ') || '-'}</div>
                  <div><strong>Trees:</strong> {site.tree_count || 0}</div>
                  <div><strong>Condition:</strong> <span style={{ textTransform: 'capitalize' }}>{site.current_condition?.replace(/_/g, ' ') || '-'}</span></div>
                  <div><strong>Active Adoptions:</strong> {site.active_adoptions || 0}</div>
                  <div><strong>Annual Cost:</strong> INR {Number(site.annual_maintenance_cost || 0).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Adoption Reports – submit and list
   ────────────────────────────────────────────── */
function AdoptionMaintenanceReports() {
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    adoption_id: '',
    report_quarter: '',
    report_type: 'quarterly',
    tree_health_status: 'good',
    trees_healthy: '',
    trees_needing_attention: '',
    trees_replaced: '',
    maintenance_activities: '',
    description: '',
  });

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await adoptionAPI.getAgencyReports({ page: p, per_page: 15 });
      setReports(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load adoption reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  const resetForm = () => {
    setForm({
      adoption_id: '', report_quarter: '', report_type: 'quarterly',
      tree_health_status: 'good', trees_healthy: '', trees_needing_attention: '',
      trees_replaced: '', maintenance_activities: '', description: '',
    });
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!form.adoption_id || !form.report_quarter) {
      toast.warn('Adoption ID and report quarter are required');
      return;
    }
    setSubmitting(true);
    try {
      await adoptionAPI.submitMaintenanceReport({
        adoption_id: Number(form.adoption_id),
        report_quarter: form.report_quarter,
        report_type: form.report_type,
        tree_health_status: form.tree_health_status,
        trees_healthy: form.trees_healthy ? Number(form.trees_healthy) : 0,
        trees_needing_attention: form.trees_needing_attention ? Number(form.trees_needing_attention) : 0,
        trees_replaced: form.trees_replaced ? Number(form.trees_replaced) : 0,
        maintenance_activities: form.maintenance_activities || undefined,
        description: form.description || undefined,
      });
      toast.success('Maintenance report submitted!');
      setShowModal(false);
      resetForm();
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading reports...</div>;

  return (
    <div>
      <div className="page-header">
        <h3>Adoption Reports</h3>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          Submit Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="empty-state">No adoption reports submitted yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Quarter</th>
                <th>Type</th>
                <th>Health Status</th>
                <th>Healthy</th>
                <th>Attention</th>
                <th>Replaced</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td>{r.report_quarter}</td>
                  <td style={{ textTransform: 'capitalize' }}>{r.report_type?.replace(/_/g, ' ')}</td>
                  <td style={{ textTransform: 'capitalize' }}>{r.tree_health_status?.replace(/_/g, ' ')}</td>
                  <td>{r.trees_healthy || 0}</td>
                  <td>{r.trees_needing_attention || 0}</td>
                  <td>{r.trees_replaced || 0}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} pages={pages} onPageChange={setPage} />

      {/* Submit Report Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h4>Submit Adoption Report</h4>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitReport}>
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Adoption ID *</label>
                    <input type="number" min="1" value={form.adoption_id}
                      onChange={(e) => setForm({ ...form, adoption_id: e.target.value })}
                      placeholder="Adoption ID" required />
                  </div>
                  <div className="form-group">
                    <label>Report Quarter *</label>
                    <input type="text" value={form.report_quarter}
                      onChange={(e) => setForm({ ...form, report_quarter: e.target.value })}
                      placeholder="e.g. Q1-2026" required />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Report Type</label>
                    <select value={form.report_type}
                      onChange={(e) => setForm({ ...form, report_type: e.target.value })}>
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                      <option value="incident">Incident</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tree Health Status</label>
                    <select value={form.tree_health_status}
                      onChange={(e) => setForm({ ...form, tree_health_status: e.target.value })}>
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Trees Healthy</label>
                    <input type="number" min="0" value={form.trees_healthy}
                      onChange={(e) => setForm({ ...form, trees_healthy: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Needing Attention</label>
                    <input type="number" min="0" value={form.trees_needing_attention}
                      onChange={(e) => setForm({ ...form, trees_needing_attention: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Trees Replaced</label>
                    <input type="number" min="0" value={form.trees_replaced}
                      onChange={(e) => setForm({ ...form, trees_replaced: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Maintenance Activities</label>
                  <textarea rows={3} value={form.maintenance_activities}
                    onChange={(e) => setForm({ ...form, maintenance_activities: e.target.value })}
                    placeholder="e.g. Watering, pruning, pest treatment..." />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="General observations and notes..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Helper: formatDate
   ────────────────────────────────────────────── */
function agencyFormatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/* ──────────────────────────────────────────────
   Modal wrapper (Agency)
   ────────────────────────────────────────────── */
function AgencyModal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Agency Post Idea Modal
   ────────────────────────────────────────────── */
const AGENCY_INITIAL_IDEA_FORM = { title: '', description: '', category: '' };

function AgencyPostIdeaModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(AGENCY_INITIAL_IDEA_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.warn('Please provide a title and description');
      return;
    }
    setSubmitting(true);
    try {
      await agencyAPI.postIdea(form);
      toast.success('Idea posted successfully!');
      setForm(AGENCY_INITIAL_IDEA_FORM);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post idea');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AgencyModal open={open} title="Post New Idea" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label>Title *</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Give your idea a clear title" required />
        </div>
        <div className="form-group">
          <label>Description *</label>
          <textarea name="description" value={form.description} onChange={handleChange} rows={5} placeholder="Describe your idea in detail..." required />
        </div>
        <div className="form-group">
          <label>Category</label>
          <input type="text" name="category" value={form.category} onChange={handleChange} placeholder="e.g. Plantation Technique, Community, Innovation" />
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Posting...' : 'Post Idea'}
          </button>
        </div>
      </form>
    </AgencyModal>
  );
}

/* ──────────────────────────────────────────────
   Agency Ideas List
   ────────────────────────────────────────────── */
function AgencyIdeas() {
  const [ideas, setIdeas] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await agencyAPI.getMyIdeas({ page, per_page: 10 });
      setIdeas(res.data.ideas || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h3>Collaboration Ideas</h3>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>Post New Idea</button>
      </div>

      <AgencyPostIdeaModal open={showModal} onClose={() => setShowModal(false)} onCreated={() => { setPage(1); load(); }} />

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : ideas.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">You have not posted any ideas yet.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>Share Your First Idea</button>
        </div>
      ) : (
        <>
          <div className="cards-grid">
            {ideas.map((idea) => (
              <div key={idea.id} className="idea-card">
                <div className="idea-card-header">
                  <h4>{idea.title}</h4>
                  <StatusBadge status={idea.status} />
                </div>
                <p className="text-muted">
                  {idea.description?.substring(0, 200)}{idea.description?.length > 200 ? '...' : ''}
                </p>
                <div className="idea-meta">
                  {idea.category && <span className="idea-category">{idea.category}</span>}
                  <span className="text-muted">{agencyFormatDate(idea.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Monitoring Progress (GPS Map, Zone Cards, Stats, Photos)
   ────────────────────────────────────────────── */
function AgencyMonitoringProgress() {
  const HISTORY_PER_PAGE = 5;
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxEntry, setLightboxEntry] = useState('');

  // Reset page when filter changes
  useEffect(() => { setHistoryPage(1); }, [selectedTargetId, selectedZoneId]);

  useEffect(() => {
    agencyAPI.getMonitoringSummary()
      .then(res => setSummary(res.data))
      .catch(() => toast.error('Failed to load monitoring summary'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner">Loading monitoring data...</div>;

  const allZones = summary.monitoring_zones || [];
  const allEntries = summary.monitoring_entries || [];
  const targetsInfo = summary.targets_info || [];

  const targetNameMap = {};
  targetsInfo.forEach(t => { targetNameMap[t.id] = t.title; });

  const zones = selectedTargetId ? allZones.filter(z => z.target_id === parseInt(selectedTargetId)) : allZones;
  const entries = selectedTargetId ? allEntries.filter(e => e.target_id === parseInt(selectedTargetId)) : allEntries;

  // Approved-only, dedupe to ONE latest entry per zone
  const approvedEntries = entries.filter(e => e.status === 'approved');
  const _byZone = {};
  approvedEntries.forEach(e => {
    if (!e.zone_id || !e.monitoring_date) return;
    const cur = _byZone[e.zone_id];
    if (!cur || e.monitoring_date > cur.monitoring_date ||
        (e.monitoring_date === cur.monitoring_date && (e.id || 0) > (cur.id || 0))) {
      _byZone[e.zone_id] = e;
    }
  });
  const latestEntries = [...Object.values(_byZone), ...approvedEntries.filter(e => !e.zone_id)];
  const fHealthy = latestEntries.reduce((s, e) => s + (e.trees_healthy || 0), 0);
  const fAttention = latestEntries.reduce((s, e) => s + (e.trees_needs_attention || 0), 0);
  const fCritical = latestEntries.reduce((s, e) => s + (e.trees_critical || 0), 0);
  const fDead = latestEntries.reduce((s, e) => s + (e.trees_dead || 0), 0);
  // Trees Monitored = reporter-entered total (may exceed sum of health buckets if some trees are unclassified)
  const fTotalMonitored = latestEntries.reduce((s, e) => s + (e.total_trees_monitored || 0), 0);
  // Survival = (Healthy + Need Attention + Critical) / target planted total. Target = sum of total_plants for selected projects.
  const filteredTargets = selectedTargetId
    ? targetsInfo.filter(t => t.id === parseInt(selectedTargetId))
    : targetsInfo;
  const fTargetTotal = filteredTargets.reduce((s, t) => s + (t.total_plants || 0), 0);
  const fAliveTrees = fHealthy + fAttention + fCritical;
  const fAvgSurvival = fTargetTotal > 0 ? ((fAliveTrees / fTargetTotal) * 100).toFixed(2) : 0;
  const fAgencies = new Set(entries.map(e => e.agency_user_id)).size;
  const fTargets = new Set(entries.map(e => e.target_id)).size;
  const fPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);

  // Per-zone tree count from the latest approved entry
  const zoneTreeCount = {};
  latestEntries.forEach(e => {
    if (e.zone_id) zoneTreeCount[e.zone_id] = e.total_trees_monitored || 0;
  });

  const statusColors = { pending: '#ff9800', approved: '#4caf50', disbursed: '#2196f3', submitted: '#ff9800', rejected: '#ef5350', under_review: '#ff9800', draft: '#9e9e9e' };
  const healthColors = { excellent: '#1b5e20', good: '#4caf50', fair: '#ff9800', poor: '#f44336', critical: '#b71c1c' };
  const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
  const zoneColorMap = {};
  zones.forEach((z, i) => { zoneColorMap[z.id] = zoneColors[i % zoneColors.length]; });

  const fmt = n => (n || 0).toLocaleString();
  const pct = (v, max) => max > 0 ? `${((v / max) * 100).toFixed(1)}%` : '0%';

  // Collect map points
  const allPoints = [];
  zones.forEach(z => {
    if (z.boundary_polygon) z.boundary_polygon.forEach(p => allPoints.push([p.lat, p.lng]));
    if (z.center_latitude && z.center_longitude) allPoints.push([z.center_latitude, z.center_longitude]);
  });
  entries.forEach(e => {
    if (e.latitude && e.longitude) allPoints.push([e.latitude, e.longitude]);
    (e.photos || []).forEach(p => { if (p.latitude && p.longitude) allPoints.push([p.latitude, p.longitude]); });
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0, color: '#1b5e20' }}>Monitoring Progress</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="agency-project-filter" style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Project:</label>
          <select id="agency-project-filter" value={selectedTargetId} onChange={e => { setSelectedTargetId(e.target.value); setSelectedZoneId(null); }}
            style={{ padding: '10px 16px', borderRadius: 10, border: '2px solid #1b5e20', background: '#fff', fontSize: 14, minWidth: 260, fontWeight: 500, color: '#1b5e20', cursor: 'pointer' }}>
            <option value="">All Projects ({targetsInfo.length})</option>
            {targetsInfo.map(t => (
              <option key={t.id} value={t.id}>{t.title} — {[t.city, t.state].filter(Boolean).join(', ')}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Trees Monitored', value: fmt(fTotalMonitored), sub: `${fTargets} targets`, color: '#4caf50', icon: '\uD83C\uDF33' },
          { label: 'Avg Survival Rate', value: `${fAvgSurvival}%`, sub: `${entries.length} entries`, color: fAvgSurvival >= 80 ? '#4caf50' : '#ff9800', icon: '\uD83D\uDCC8' },
          { label: 'Agencies Reporting', value: fmt(fAgencies), sub: `${fmt(fPhotos)} photos`, color: '#1976d2', icon: '\uD83C\uDFE2' },
          { label: 'Trees Healthy', value: fmt(fHealthy), sub: pct(fHealthy, fTotalMonitored) + ' of monitored', color: '#2e7d32', icon: '\u2705' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: '#e8f5e9', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{kpi.icon} {kpi.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1b5e20' }}>{kpi.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Tree Health Summary */}
      <div className="card" style={{ padding: 28, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Tree Health Summary</h3>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
          <span>Healthy: <strong style={{ color: '#4caf50' }}>{fmt(fHealthy)}</strong></span>
          <span>Needs Attention: <strong style={{ color: '#ff9800' }}>{fmt(fAttention)}</strong></span>
          <span>Critical: <strong style={{ color: '#f44336' }}>{fmt(fCritical)}</strong></span>
          <span>Dead: <strong style={{ color: '#999' }}>{fmt(fDead)}</strong></span>
          <span>Total: <strong>{fmt(fTotalMonitored)}</strong></span>
          {fTotalMonitored > 0 && <span>Survival: <strong style={{ color: '#1b5e20' }}>{fAvgSurvival}%</strong></span>}
        </div>
      </div>

      {/* GPS Map */}
      {allPoints.length > 0 && (() => {
        const bounds = L.latLngBounds(allPoints);
        return (
          <div className="card" style={{ padding: 28, marginBottom: 24 }}>
            <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring & GPS Mapping</h3>
            <div style={{ height: 450, borderRadius: 12, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <MapContainer bounds={bounds.pad(0.1)} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Esri" />
                <ZoneFlyTo zones={zones} entries={entries} selectedZoneId={selectedZoneId} />
                {zones.map((z) => z.boundary_polygon && (
                  <Polygon key={z.id} positions={z.boundary_polygon.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: zoneColorMap[z.id], weight: selectedZoneId === z.id ? 5 : 3, fillOpacity: selectedZoneId === z.id ? 0.35 : 0.2 }}>
                    <Popup><strong>{z.name}</strong><br />{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees{z.area_sqm ? ` | ${Number(z.area_sqm).toLocaleString()} sq m` : ''}</Popup>
                  </Polygon>
                ))}
                {(() => {
                  const groupMap = new Map();
                  entries.forEach(e => {
                    (e.photos || []).forEach(p => {
                      if (!(p.latitude && p.longitude)) return;
                      const key = e.zone_id ? `zone-${e.zone_id}` : `entry-${e.id}`;
                      if (!groupMap.has(key)) {
                        groupMap.set(key, {
                          zoneId: e.zone_id || null,
                          label: e.zone_name || 'Monitoring Entry',
                          color: zoneColorMap[e.zone_id] || '#e65100',
                          photos: [],
                        });
                      }
                      groupMap.get(key).photos.push({
                        id: p.id,
                        url: getPhotoUrl(p.photo_url),
                        type: p.photo_type,
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                        zoneName: e.zone_name,
                        entryDate: e.monitoring_date,
                      });
                    });
                  });
                  const groups = [];
                  groupMap.forEach((g, key) => {
                    if (g.photos.length === 0) return;
                    const cLat = g.photos.reduce((s, p) => s + p.lat, 0) / g.photos.length;
                    const cLng = g.photos.reduce((s, p) => s + p.lng, 0) / g.photos.length;
                    groups.push({ key, centroidLat: cLat, centroidLng: cLng, ...g });
                  });
                  return groups.map(g => {
                    const isSelected = selectedZoneId && g.zoneId === selectedZoneId;
                    const size = isSelected ? 34 : 26;
                    const count = g.photos.length;
                    return (
                      <Marker key={g.key} position={[g.centroidLat, g.centroidLng]}
                        icon={L.divIcon({
                          className: '',
                          html: `<div style="background:${g.color};color:#fff;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 0 6px rgba(0,0,0,0.5);font-size:${size >= 30 ? 13 : 11}px;font-weight:700;${isSelected ? 'animation:pulse 1s infinite;' : ''}">${count}</div>`,
                          iconSize: [size, size],
                          iconAnchor: [size/2, size/2],
                        })}>
                        <Popup maxWidth={320} minWidth={280}>
                          <PhotoCarousel
                            photos={g.photos}
                            label={g.label}
                            centroidLat={g.centroidLat}
                            centroidLng={g.centroidLng}
                          />
                        </Popup>
                      </Marker>
                    );
                  });
                })()}
              </MapContainer>
            </div>
            {/* Clickable zone legend */}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
              <span onClick={() => setSelectedZoneId(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: !selectedZoneId ? '#e8f5e9' : '#fff', border: !selectedZoneId ? '2px solid #4caf50' : '1px solid #e0e0e0', fontWeight: !selectedZoneId ? 700 : 400 }}>
                All Zones
              </span>
              {zones.map((z) => (
                <span key={z.id} onClick={() => setSelectedZoneId(selectedZoneId === z.id ? null : z.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: selectedZoneId === z.id ? zoneColorMap[z.id] + '22' : '#fff', border: selectedZoneId === z.id ? `2px solid ${zoneColorMap[z.id]}` : '1px solid #e0e0e0', fontWeight: selectedZoneId === z.id ? 700 : 400 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: zoneColorMap[z.id], display: 'inline-block' }} />
                  {z.name} ({(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees)
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Monitoring Zone Cards */}
      {zones.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring Zones <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>— click a zone to see it on the map</span></h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {zones.map((z, i) => {
              const isActive = selectedZoneId === z.id;
              const zColor = zoneColors[i % zoneColors.length];
              return (
              <div key={z.id} onClick={() => { setSelectedZoneId(isActive ? null : z.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{ border: isActive ? `2px solid ${zColor}` : '1px solid #e0e0e0', borderRadius: 12, padding: 16, background: isActive ? zColor + '0a' : '#fafafa', cursor: 'pointer', transition: 'all 0.25s', transform: isActive ? 'scale(1.02)' : 'scale(1)', boxShadow: isActive ? `0 4px 16px ${zColor}33` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, background: zColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#1b5e20', fontSize: 15 }}>{z.name}</span>
                  {isActive && <span style={{ marginLeft: 'auto', fontSize: 11, background: zColor, color: '#fff', padding: '2px 8px', borderRadius: 10 }}>Viewing on map</span>}
                </div>
                {z.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{z.description}</div>}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>Trees: <strong>{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()}</strong></span>
                  {z.area_sqm && <span>Area: <strong>{Number(z.area_sqm).toLocaleString()}</strong> sq m</span>}
                  <span style={{ textTransform: 'capitalize', color: '#888' }}>{z.zone_type?.replace('_', ' ')}</span>
                </div>
                {z.boundary_polygon ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#4caf50', fontWeight: 600 }}>GPS Boundary Mapped ({z.boundary_polygon.length} points)</div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>GPS boundary not mapped yet</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>{z.entries_count || 0} monitoring {(z.entries_count || 0) === 1 ? 'entry' : 'entries'}</div>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Monitoring Entries with Photos */}
      {entries.length > 0 && (() => {
        const totalPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);
        return (
          <div style={{ display: 'flex', gap: 24, marginBottom: 20, padding: '12px 16px', background: '#f5f5f5', borderRadius: 10, fontSize: 13, flexWrap: 'wrap' }}>
            <span>Total Visits: <strong>{entries.length}</strong></span>
            <span>Trees Monitored: <strong>{fTotalMonitored.toLocaleString()}</strong></span>
            {fAvgSurvival > 0 && <span>Avg Survival: <strong>{fAvgSurvival}%</strong></span>}
            <span>Photos: <strong>{totalPhotos}</strong></span>
          </div>
        );
      })()}

      {entries.length > 0 && (() => {
        const historyPages = Math.max(1, Math.ceil(entries.length / HISTORY_PER_PAGE));
        const currentPage = Math.min(historyPage, historyPages);
        const pagedEntries = entries.slice((currentPage - 1) * HISTORY_PER_PAGE, currentPage * HISTORY_PER_PAGE);
        return (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, color: '#1b5e20' }}>Monitoring History</h3>
            <span style={{ fontSize: 12, color: '#888' }}>
              Showing {(currentPage - 1) * HISTORY_PER_PAGE + 1}&ndash;{Math.min(currentPage * HISTORY_PER_PAGE, entries.length)} of {entries.length}
            </span>
          </div>
          {pagedEntries.map(e => {
            const photos = e.photos || [];
            return (
              <div key={e.id} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 16, marginBottom: 16, background: '#fafffe', marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    {!selectedTargetId && targetNameMap[e.target_id] && (
                      <div style={{ fontSize: 11, color: '#1976d2', fontWeight: 600, marginBottom: 3, background: '#e3f2fd', display: 'inline-block', padding: '2px 8px', borderRadius: 6 }}>
                        {targetNameMap[e.target_id]}
                      </div>
                    )}
                    <div>
                      <strong style={{ fontSize: 14 }}>{e.zone_name || 'Unknown Zone'}</strong>
                      <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{e.monitoring_date ? new Date(e.monitoring_date).toLocaleDateString() : ''}</span>
                      {e.agency_name && <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>by {e.agency_name}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: healthColors[e.overall_health] || '#666', background: (healthColors[e.overall_health] || '#666') + '18', textTransform: 'capitalize' }}>{e.overall_health}</span>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[e.status] || '#666', background: (statusColors[e.status] || '#666') + '18' }}>{e.status}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>Trees: <strong>{(e.total_trees_monitored || 0).toLocaleString()}</strong></span>
                  <span>Healthy: <strong style={{ color: '#4caf50' }}>{e.trees_healthy || 0}</strong></span>
                  <span>Attention: <strong style={{ color: '#ff9800' }}>{e.trees_needs_attention || 0}</strong></span>
                  <span>Critical: <strong style={{ color: '#f44336' }}>{e.trees_critical || 0}</strong></span>
                  <span>Dead: <strong style={{ color: '#999' }}>{e.trees_dead || 0}</strong></span>
                  {e.survival_rate != null && <span>Survival: <strong>{e.survival_rate}%</strong></span>}
                </div>
                {e.notes && <div style={{ marginTop: 8, fontSize: 13, color: '#555', fontStyle: 'italic' }}>{e.notes}</div>}
                {e.latitude && e.longitude && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#1976d2' }}>
                    📍 Entry GPS: {Number(e.latitude).toFixed(6)}, {Number(e.longitude).toFixed(6)}
                  </div>
                )}
                {photos.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>Photos ({photos.length})</div>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                      {photos.map((p, pIdx) => (
                        <div key={p.id} onClick={() => { setLightboxPhotos(photos); setLightboxIndex(pIdx); setLightboxEntry(e.zone_name || ''); }}
                          style={{ flexShrink: 0, textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ position: 'relative', width: 100, height: 75 }}>
                            <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type}
                              style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, border: '2px solid #e0e0e0' }}
                              onError={ev => { ev.target.style.display = 'none'; }} />
                            {p.latitude && p.longitude && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 8, padding: '2px 3px', borderRadius: '0 0 6px 6px', lineHeight: 1.2, textAlign: 'center' }}>
                                {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2, textTransform: 'capitalize' }}>{p.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {historyPages > 1 && (
            <div style={{ marginTop: 16 }}>
              <Pagination page={currentPage} pages={historyPages} onPageChange={setHistoryPage} />
            </div>
          )}
        </div>
        );
      })()}

      {entries.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'\uD83D\uDCCA'}</div>
          <h3 style={{ color: '#1b5e20', margin: '0 0 8px' }}>No Monitoring Data Yet</h3>
          <p style={{ color: '#888' }}>Monitoring entries will appear here once field reports are submitted.</p>
        </div>
      )}
      {lightboxPhotos && <PhotoLightbox photos={lightboxPhotos} initialIndex={lightboxIndex} entryInfo={lightboxEntry} onClose={() => setLightboxPhotos(null)} />}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Zone Management – CRUD for monitoring zones
   ────────────────────────────────────────────── */
function ZoneManagement() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    target_id: '', name: '', description: '', tree_count: '', zone_type: 'pre_defined',
    center_latitude: '', center_longitude: '', area_sqm: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await monitoringAPI.getZones();
      setZones(res.data.items || res.data.zones || []);
    } catch {
      toast.error('Failed to load monitoring zones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadProjects = async () => {
    try {
      const res = await agencyAPI.getProjects({ per_page: 100 });
      setProjects(res.data.allocations || res.data.items || []);
    } catch { /* ignore */ }
  };

  const resetForm = () => {
    setForm({ target_id: '', name: '', description: '', tree_count: '', zone_type: 'pre_defined', center_latitude: '', center_longitude: '', area_sqm: '' });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.target_id || !form.name) { toast.warn('Project and zone name are required'); return; }
    setSubmitting(true);
    try {
      await monitoringAPI.createZone({
        target_id: Number(form.target_id),
        name: form.name,
        description: form.description || undefined,
        tree_count: form.tree_count ? Number(form.tree_count) : 0,
        zone_type: form.zone_type,
        center_latitude: form.center_latitude ? Number(form.center_latitude) : undefined,
        center_longitude: form.center_longitude ? Number(form.center_longitude) : undefined,
        area_sqm: form.area_sqm ? Number(form.area_sqm) : undefined,
      });
      toast.success('Monitoring zone created!');
      setShowModal(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create zone');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading monitoring zones...</div>;

  return (
    <div>
      <div className="page-header">
        <h3>Monitoring Zones</h3>
        <button className="btn btn-primary" onClick={() => { resetForm(); loadProjects(); setShowModal(true); }}>
          Create Zone
        </button>
      </div>

      {zones.length === 0 ? (
        <div className="empty-state">No monitoring zones created yet. Create one to start group monitoring.</div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Zone Name</th>
                <th>Project</th>
                <th>Type</th>
                <th>Tree Count</th>
                <th>Entries</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id}>
                  <td><strong>{z.name}</strong></td>
                  <td>{z.target_title || z.target?.title || `Project #${z.target_id}`}</td>
                  <td><span style={{ textTransform: 'capitalize' }}>{z.zone_type?.replace(/_/g, ' ')}</span></td>
                  <td>{z.tree_count?.toLocaleString()}</td>
                  <td>{z.entries_count || 0}</td>
                  <td><StatusBadge status={z.status} /></td>
                  <td>{agencyFormatDate(z.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Zone Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Create Monitoring Zone</h4>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Project *</label>
                  <select value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} required>
                    <option value="">Select project...</option>
                    {projects.map((p) => (
                      <option key={p.target_id || p.id} value={p.target_id || p.id}>
                        {p.target?.title || p.target_title || p.title || `Project #${p.target_id || p.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Zone Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Block A - North Sector" required />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe this monitoring zone..." />
                </div>
                <div className="form-group">
                  <label>Zone Type</label>
                  <select value={form.zone_type} onChange={(e) => setForm({ ...form, zone_type: e.target.value })}>
                    <option value="pre_defined">Pre-defined Zone</option>
                    <option value="ad_hoc">Ad-hoc Group</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tree Count</label>
                  <input type="number" min="0" value={form.tree_count} onChange={(e) => setForm({ ...form, tree_count: e.target.value })} placeholder="Number of trees in this zone" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Center Latitude</label>
                    <input type="number" step="any" value={form.center_latitude} onChange={(e) => setForm({ ...form, center_latitude: e.target.value })} placeholder="e.g. 17.3850" />
                  </div>
                  <div className="form-group">
                    <label>Center Longitude</label>
                    <input type="number" step="any" value={form.center_longitude} onChange={(e) => setForm({ ...form, center_longitude: e.target.value })} placeholder="e.g. 78.4867" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Area (sq meters)</label>
                  <input type="number" min="0" step="any" value={form.area_sqm} onChange={(e) => setForm({ ...form, area_sqm: e.target.value })} placeholder="e.g. 5000" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Zone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Group Monitoring – submit and list entries
   ────────────────────────────────────────────── */
function GroupMonitoring() {
  const [entries, setEntries] = useState([]);
  const [zones, setZones] = useState([]);
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);

  const [form, setForm] = useState({
    target_id: '', zone_id: '', monitoring_date: new Date().toISOString().split('T')[0],
    total_trees_monitored: '', trees_healthy: '', trees_needs_attention: '',
    trees_critical: '', trees_dead: '', overall_health: 'good', notes: '',
    latitude: '', longitude: '',
    ad_hoc_zone_name: '', ad_hoc_tree_count: '',
    samples: [],
  });

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 15 };
      if (statusFilter) params.status = statusFilter;
      const res = await monitoringAPI.getEntries(params);
      setEntries(res.data.items || res.data.entries || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load monitoring entries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(page); }, [page, load]);

  const loadFormData = async () => {
    try {
      const [zRes, pRes] = await Promise.all([
        monitoringAPI.getZones(),
        agencyAPI.getProjects({ per_page: 100 }),
      ]);
      setZones(zRes.data.items || zRes.data.zones || []);
      setProjects(pRes.data.allocations || pRes.data.items || []);
    } catch { /* ignore */ }
  };

  const resetForm = () => {
    setForm({
      target_id: '', zone_id: '', monitoring_date: new Date().toISOString().split('T')[0],
      total_trees_monitored: '', trees_healthy: '', trees_needs_attention: '',
      trees_critical: '', trees_dead: '', overall_health: 'good', notes: '',
      latitude: '', longitude: '', ad_hoc_zone_name: '', ad_hoc_tree_count: '',
      samples: [],
    });
  };

  const computeSurvival = () => {
    const total = Number(form.total_trees_monitored) || 0;
    const dead = Number(form.trees_dead) || 0;
    if (total === 0) return null;
    return (((total - dead) / total) * 100).toFixed(1);
  };

  const addSample = () => {
    if (form.samples.length >= 10) { toast.warn('Maximum 10 samples allowed'); return; }
    setForm({
      ...form, samples: [...form.samples, {
        sample_number: form.samples.length + 1,
        tree_identifier: '', height_cm: '', girth_cm: '', health_status: 'healthy', notes: '',
      }],
    });
  };

  const updateSample = (idx, field, value) => {
    const updated = [...form.samples];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, samples: updated });
  };

  const removeSample = (idx) => {
    const updated = form.samples.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sample_number: i + 1 }));
    setForm({ ...form, samples: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.target_id) { toast.warn('Please select a project'); return; }
    if (!form.total_trees_monitored) { toast.warn('Please enter total trees monitored'); return; }
    setSubmitting(true);
    try {
      const payload = {
        target_id: Number(form.target_id),
        monitoring_date: form.monitoring_date,
        total_trees_monitored: Number(form.total_trees_monitored),
        trees_healthy: Number(form.trees_healthy) || 0,
        trees_needs_attention: Number(form.trees_needs_attention) || 0,
        trees_critical: Number(form.trees_critical) || 0,
        trees_dead: Number(form.trees_dead) || 0,
        overall_health: form.overall_health,
        notes: form.notes || undefined,
        latitude: form.latitude ? Number(form.latitude) : undefined,
        longitude: form.longitude ? Number(form.longitude) : undefined,
        samples: form.samples.map((s) => ({
          sample_number: s.sample_number,
          tree_identifier: s.tree_identifier || undefined,
          height_cm: s.height_cm ? Number(s.height_cm) : undefined,
          girth_cm: s.girth_cm ? Number(s.girth_cm) : undefined,
          health_status: s.health_status,
          notes: s.notes || undefined,
        })),
      };
      if (form.zone_id) {
        payload.zone_id = Number(form.zone_id);
      } else if (form.ad_hoc_zone_name) {
        payload.ad_hoc_zone_name = form.ad_hoc_zone_name;
        payload.ad_hoc_tree_count = Number(form.ad_hoc_tree_count) || 0;
      }
      const survival = computeSurvival();
      if (survival !== null) payload.survival_rate = Number(survival);
      await monitoringAPI.submitEntry(payload);
      toast.success('Group monitoring entry submitted!');
      setShowModal(false);
      resetForm();
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); setExpandedEntry(null); return; }
    try {
      const res = await monitoringAPI.getEntry(id);
      setExpandedEntry(res.data.entry || res.data);
      setExpandedId(id);
    } catch {
      toast.error('Failed to load entry details');
    }
  };

  const healthColor = (health) => {
    const map = { excellent: '#059669', good: '#10b981', fair: '#f59e0b', poor: '#ef4444', critical: '#dc2626' };
    return map[health] || '#6b7280';
  };

  if (loading) return <div className="loading">Loading group monitoring entries...</div>;

  return (
    <div>
      <div className="page-header">
        <h3>Group Monitoring</h3>
        <button className="btn btn-primary" onClick={() => { resetForm(); loadFormData(); setShowModal(true); }}>
          Submit Entry
        </button>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['', 'draft', 'submitted', 'approved', 'rejected'].map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
              border: statusFilter === s ? '2px solid #166534' : '1px solid #d1d5db',
              background: statusFilter === s ? '#166534' : '#fff',
              color: statusFilter === s ? '#fff' : '#374151', fontWeight: statusFilter === s ? 600 : 400,
            }}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {entries.length === 0 ? (
        <div className="empty-state">No group monitoring entries found.</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th></th>
                  <th>Zone / Group</th>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Trees</th>
                  <th>Health Breakdown</th>
                  <th>Survival</th>
                  <th>Health</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr onClick={() => toggleExpand(entry.id)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontSize: 12 }}>{expandedId === entry.id ? '▼' : '▶'}</td>
                      <td><strong>{entry.zone_name || 'Ad-hoc'}</strong></td>
                      <td>{entry.target_title || `#${entry.target_id}`}</td>
                      <td>{agencyFormatDate(entry.monitoring_date)}</td>
                      <td>{entry.total_trees_monitored}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                          <span style={{ color: '#059669' }} title="Healthy">✓{entry.trees_healthy || 0}</span>
                          <span style={{ color: '#f59e0b' }} title="Needs Attention">⚠{entry.trees_needs_attention || 0}</span>
                          <span style={{ color: '#ef4444' }} title="Critical">✗{entry.trees_critical || 0}</span>
                          <span style={{ color: '#6b7280' }} title="Dead">☠{entry.trees_dead || 0}</span>
                        </div>
                      </td>
                      <td>{entry.survival_rate != null ? `${entry.survival_rate}%` : '-'}</td>
                      <td>
                        <span style={{ color: healthColor(entry.overall_health), fontWeight: 600, textTransform: 'capitalize', fontSize: 13 }}>
                          {entry.overall_health || '-'}
                        </span>
                      </td>
                      <td><StatusBadge status={entry.status} /></td>
                    </tr>
                    {/* Expanded detail row */}
                    {expandedId === entry.id && expandedEntry && (
                      <tr>
                        <td colSpan={9} style={{ background: '#f9fafb', padding: 20 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                            <div>
                              <h5 style={{ margin: '0 0 10px' }}>Entry Details</h5>
                              <div style={{ fontSize: 14, lineHeight: 2 }}>
                                <div><strong>Agency:</strong> {expandedEntry.agency_name || '-'}</div>
                                <div><strong>Notes:</strong> {expandedEntry.notes || 'None'}</div>
                                <div><strong>GPS:</strong> {expandedEntry.latitude && expandedEntry.longitude ? `${expandedEntry.latitude}, ${expandedEntry.longitude}` : 'Not recorded'}</div>
                                {expandedEntry.review_notes && <div><strong>Review Notes:</strong> {expandedEntry.review_notes}</div>}
                              </div>
                            </div>
                            <div>
                              <h5 style={{ margin: '0 0 10px' }}>Sample Measurements ({expandedEntry.samples?.length || 0})</h5>
                              {expandedEntry.samples && expandedEntry.samples.length > 0 ? (
                                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                                  <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>#</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>ID</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Height</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Girth</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Health</th>
                                  </tr></thead>
                                  <tbody>
                                    {expandedEntry.samples.map((s, i) => (
                                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '4px 8px' }}>{s.sample_number}</td>
                                        <td style={{ padding: '4px 8px' }}>{s.tree_identifier || '-'}</td>
                                        <td style={{ padding: '4px 8px' }}>{s.height_cm ? `${s.height_cm} cm` : '-'}</td>
                                        <td style={{ padding: '4px 8px' }}>{s.girth_cm ? `${s.girth_cm} cm` : '-'}</td>
                                        <td style={{ padding: '4px 8px', textTransform: 'capitalize' }}>{s.health_status?.replace(/_/g, ' ')}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : <p style={{ color: '#9ca3af', fontSize: 13 }}>No samples recorded.</p>}
                            </div>
                          </div>
                          {/* Photos */}
                          {expandedEntry.photos && expandedEntry.photos.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                              <h5 style={{ margin: '0 0 10px' }}>Photos ({expandedEntry.photos.length})</h5>
                              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {expandedEntry.photos.map((p, i) => (
                                  <div key={i} style={{ textAlign: 'center' }}>
                                    <a href={getPhotoUrl(p.photo_url)} target="_blank" rel="noopener noreferrer">
                                      <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type} style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                    </a>
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, textTransform: 'capitalize' }}>
                                      {p.photo_type}{p.latitude ? ` 📍` : ''}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}

      {/* Submit Group Monitoring Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h4>Submit Group Monitoring Entry</h4>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Project & Zone */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Project *</label>
                    <select value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} required>
                      <option value="">Select project...</option>
                      {projects.map((p) => (
                        <option key={p.target_id || p.id} value={p.target_id || p.id}>
                          {p.target?.title || p.target_title || p.title || `Project #${p.target_id || p.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zone (optional)</label>
                    <select value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value })}>
                      <option value="">Ad-hoc (no zone)</option>
                      {zones.filter((z) => !form.target_id || String(z.target_id) === String(form.target_id)).map((z) => (
                        <option key={z.id} value={z.id}>{z.name} ({z.tree_count} trees)</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Ad-hoc zone name if no zone selected */}
                {!form.zone_id && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label>Ad-hoc Group Name</label>
                      <input type="text" value={form.ad_hoc_zone_name} onChange={(e) => setForm({ ...form, ad_hoc_zone_name: e.target.value })} placeholder="e.g. South Block Cluster" />
                    </div>
                    <div className="form-group">
                      <label>Trees in Group</label>
                      <input type="number" min="0" value={form.ad_hoc_tree_count} onChange={(e) => setForm({ ...form, ad_hoc_tree_count: e.target.value })} placeholder="e.g. 500" />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Monitoring Date *</label>
                  <input type="date" value={form.monitoring_date} onChange={(e) => setForm({ ...form, monitoring_date: e.target.value })} required />
                </div>

                {/* Tree counts */}
                <div style={{ background: '#f0fdf4', padding: 16, borderRadius: 12, marginBottom: 16 }}>
                  <h5 style={{ margin: '0 0 12px', color: '#166534' }}>Tree Health Counts</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
                    <div className="form-group">
                      <label style={{ fontSize: 12 }}>Total *</label>
                      <input type="number" min="0" value={form.total_trees_monitored} onChange={(e) => setForm({ ...form, total_trees_monitored: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: 12, color: '#059669' }}>Healthy</label>
                      <input type="number" min="0" value={form.trees_healthy} onChange={(e) => setForm({ ...form, trees_healthy: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: 12, color: '#d97706' }}>Attention</label>
                      <input type="number" min="0" value={form.trees_needs_attention} onChange={(e) => setForm({ ...form, trees_needs_attention: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: 12, color: '#dc2626' }}>Critical</label>
                      <input type="number" min="0" value={form.trees_critical} onChange={(e) => setForm({ ...form, trees_critical: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: 12, color: '#6b7280' }}>Dead</label>
                      <input type="number" min="0" value={form.trees_dead} onChange={(e) => setForm({ ...form, trees_dead: e.target.value })} />
                    </div>
                  </div>
                  {computeSurvival() !== null && (
                    <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: '#166534' }}>
                      Survival Rate: {computeSurvival()}%
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Overall Health</label>
                  <select value={form.overall_health} onChange={(e) => setForm({ ...form, overall_health: e.target.value })}>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                {/* GPS */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="e.g. 17.3850" />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="e.g. 78.4867" />
                  </div>
                </div>

                {/* Sample Measurements */}
                <div style={{ background: '#eff6ff', padding: 16, borderRadius: 12, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h5 style={{ margin: 0, color: '#1e40af' }}>Sample Measurements ({form.samples.length}/10)</h5>
                    <button type="button" onClick={addSample} style={{ padding: '4px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #3b82f6', background: '#fff', color: '#3b82f6', cursor: 'pointer' }}>
                      + Add Sample
                    </button>
                  </div>
                  {form.samples.map((sample, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 80px 80px 1fr 30px', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', paddingBottom: 8 }}>#{sample.sample_number}</div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: 11 }}>Tree ID</label>
                        <input type="text" value={sample.tree_identifier} onChange={(e) => updateSample(idx, 'tree_identifier', e.target.value)} placeholder="e.g. T-001" style={{ fontSize: 13 }} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: 11 }}>Ht (cm)</label>
                        <input type="number" min="0" step="0.1" value={sample.height_cm} onChange={(e) => updateSample(idx, 'height_cm', e.target.value)} style={{ fontSize: 13 }} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: 11 }}>Girth (cm)</label>
                        <input type="number" min="0" step="0.1" value={sample.girth_cm} onChange={(e) => updateSample(idx, 'girth_cm', e.target.value)} style={{ fontSize: 13 }} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: 11 }}>Health</label>
                        <select value={sample.health_status} onChange={(e) => updateSample(idx, 'health_status', e.target.value)} style={{ fontSize: 13 }}>
                          <option value="healthy">Healthy</option>
                          <option value="needs_attention">Needs Attention</option>
                          <option value="critical">Critical</option>
                          <option value="dead">Dead</option>
                        </select>
                      </div>
                      <button type="button" onClick={() => removeSample(idx)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18, paddingBottom: 6 }} title="Remove">×</button>
                    </div>
                  ))}
                  {form.samples.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No samples added. Click "+ Add Sample" to record representative tree measurements.</p>}
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional observations..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Blog Categories constant
   ────────────────────────────────────────────── */
const BLOG_CATEGORIES = ['Tree Knowledge', 'Heritage', 'CSR Impact', 'Technology', 'Environment', 'Community', 'Green Offerings'];

const INITIAL_BLOG_FORM = { title: '', summary: '', content: '', category: '', tags: '', read_time_min: 5, featured_image: '' };

/* ──────────────────────────────────────────────
   My Blog Posts (reusable across dashboards)
   ────────────────────────────────────────────── */
function MyBlogPosts({ api }) {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState(INITIAL_BLOG_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 10 };
      if (statusFilter) params.status = statusFilter;
      const res = await api.getMyBlogs(params);
      setBlogs(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditPost(null);
    setForm(INITIAL_BLOG_FORM);
    setShowModal(true);
  };

  const openEdit = (post) => {
    setEditPost(post);
    setForm({
      title: post.title || '',
      summary: post.summary || '',
      content: post.content || '',
      category: post.category || '',
      tags: post.tags || '',
      read_time_min: post.read_time_min || 5,
      featured_image: post.featured_image || '',
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'read_time_min' ? parseInt(value) || 0 : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.warn('Title and content are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editPost) {
        await api.updateBlog(editPost.id, form);
        toast.success('Blog post updated successfully!');
      } else {
        await api.createBlog(form);
        toast.success('Blog post created successfully!');
      }
      setShowModal(false);
      setEditPost(null);
      setForm(INITIAL_BLOG_FORM);
      setPage(1);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save blog post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await api.deleteBlog(id);
      toast.success('Blog post deleted');
      load();
    } catch {
      toast.error('Failed to delete blog post');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h3>My Blog Posts</h3>
        <button className="btn btn-primary" onClick={openCreate}>+ Create Blog Post</button>
      </div>

      {/* Status filter buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { value: '', label: 'All' },
          { value: 'pending_review', label: 'Pending Review' },
          { value: 'published', label: 'Published' },
          { value: 'rejected', label: 'Rejected' },
        ].map(s => (
          <button key={s.value} onClick={() => { setStatusFilter(s.value); setPage(1); }}
            className={`btn btn-sm ${statusFilter === s.value ? 'btn-primary' : 'btn-secondary'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Blog editor modal */}
      <AgencyModal open={showModal} title={editPost ? 'Edit Blog Post' : 'Create Blog Post'} onClose={() => { setShowModal(false); setEditPost(null); }}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Title *</label>
            <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Blog post title" required />
          </div>
          <div className="form-group">
            <label>Summary</label>
            <textarea name="summary" value={form.summary} onChange={handleChange} rows={2} placeholder="Brief summary of your post" />
          </div>
          <div className="form-group">
            <label>Content *</label>
            <textarea name="content" value={form.content} onChange={handleChange} rows={8} placeholder="Write your blog post content..." required />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="">Select a category</option>
              {BLOG_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input type="text" name="tags" value={form.tags} onChange={handleChange} placeholder="e.g. trees, sustainability, green" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Read Time (minutes)</label>
              <input type="number" name="read_time_min" value={form.read_time_min} onChange={handleChange} min={1} max={60} />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Featured Image URL</label>
              <input type="text" name="featured_image" value={form.featured_image} onChange={handleChange} placeholder="https://example.com/image.jpg" />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditPost(null); }} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : editPost ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </AgencyModal>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : blogs.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">No blog posts found.</p>
          <button className="btn btn-primary" onClick={openCreate}>Create Your First Blog Post</button>
        </div>
      ) : (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map(post => (
                  <tr key={post.id}>
                    <td>
                      <strong>{post.title}</strong>
                      {post.status === 'rejected' && post.rejection_reason && (
                        <div style={{ fontSize: '12px', color: '#b45309', marginTop: '4px' }}>
                          Rejection reason: {post.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td>{post.category || '-'}</td>
                    <td><StatusBadge status={post.status} /></td>
                    <td>{agencyFormatDate(post.created_at)}</td>
                    <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {post.status !== 'published' && (
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(post)}>Edit</button>
                      )}
                      {post.status !== 'published' && (
                        <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }} onClick={() => handleDelete(post.id)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Field Supervisors
   ────────────────────────────────────────────── */
function FieldSupervisors() {
  const [supervisors, setSupervisors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editAssignSupervisor, setEditAssignSupervisor] = useState(null);
  const [editAssignIds, setEditAssignIds] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', employee_id: '', allocation_ids: [] });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      agencyAPI.getFieldSupervisors(),
      agencyAPI.getProjects(),
    ]).then(([supRes, projRes]) => {
      setSupervisors(supRes.data.supervisors || []);
      setProjects(projRes.data.items || []);
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAlloc = (allocId, current, setter) => {
    setter(prev => prev.includes(allocId) ? prev.filter(x => x !== allocId) : [...prev, allocId]);
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    try {
      await agencyAPI.createFieldSupervisor({
        name: form.name, email: form.email, password: form.password,
        phone: form.phone || undefined, employee_id: form.employee_id || undefined,
        allocation_ids: form.allocation_ids,
      });
      toast.success('Field supervisor created');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', phone: '', employee_id: '', allocation_ids: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to create'); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this field supervisor from your team?')) return;
    try {
      await agencyAPI.deleteFieldSupervisor(id);
      toast.success('Field supervisor removed');
      load();
    } catch { toast.error('Failed to remove'); }
  };

  const openEditAssign = (sup) => {
    const ids = (sup.field_supervisor_profile?.assigned_allocation_ids || []);
    setEditAssignIds(ids);
    setEditAssignSupervisor(sup);
  };

  const handleSaveAssignments = async () => {
    try {
      await agencyAPI.updateSupervisorAssignments(editAssignSupervisor.id, { allocation_ids: editAssignIds });
      toast.success('Assignments updated');
      setEditAssignSupervisor(null);
      load();
    } catch { toast.error('Failed to update assignments'); }
  };

  const getProjectName = (allocId) => {
    const p = projects.find(x => x.id === allocId);
    return p?.target?.title || p?.title || `Project #${allocId}`;
  };

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' };

  const ProjectCheckboxList = ({ selectedIds, onChange }) => (
    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px' }}>
      {projects.length === 0 ? (
        <div style={{ color: '#999', fontSize: 13 }}>No projects available</div>
      ) : projects.map(p => {
        const label = p.target?.title || `Project #${p.id}`;
        return (
          <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={selectedIds.includes(p.id)}
              onChange={() => onChange(p.id)} />
            {label}
          </label>
        );
      })}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Field Supervisors</h3>
        <button className="btn btn-success btn-sm" onClick={() => setShowCreate(true)}>+ Add Field Supervisor</button>
      </div>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        Field supervisors have mobile-only access to group monitoring, GPS mapping, and monitoring progress for your projects.
      </p>

      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowCreate(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Add Field Supervisor</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
              </div>
              <div>
                <label style={labelStyle}>Email *</label>
                <input style={inputStyle} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Login email" />
              </div>
              <div>
                <label style={labelStyle}>Password *</label>
                <input style={inputStyle} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Set login password" />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" />
              </div>
              <div>
                <label style={labelStyle}>Employee ID</label>
                <input style={inputStyle} value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} placeholder="Optional" />
              </div>
              <div>
                <label style={labelStyle}>Assign Projects</label>
                <ProjectCheckboxList
                  selectedIds={form.allocation_ids}
                  onChange={(id) => setForm(prev => ({
                    ...prev,
                    allocation_ids: prev.allocation_ids.includes(id)
                      ? prev.allocation_ids.filter(x => x !== id)
                      : [...prev.allocation_ids, id]
                  }))}
                />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-sm" style={{ background: '#e5e7eb' }} onClick={() => setShowCreate(false)}>Cancel</button>
                <button className="btn btn-success btn-sm" onClick={handleCreate}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editAssignSupervisor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setEditAssignSupervisor(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 420, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Edit Projects — {editAssignSupervisor.name}</h3>
            <ProjectCheckboxList
              selectedIds={editAssignIds}
              onChange={(id) => setEditAssignIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-sm" style={{ background: '#e5e7eb' }} onClick={() => setEditAssignSupervisor(null)}>Cancel</button>
              <button className="btn btn-success btn-sm" onClick={handleSaveAssignments}>Save</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="loading-spinner">Loading...</div> : (
        supervisors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f9fafb', borderRadius: 12, color: '#888' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>👷</div>
            <div style={{ fontWeight: 600 }}>No field supervisors yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Add supervisors to assign them to your field monitoring activities.</div>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Phone</th><th>Employee ID</th><th>Assigned Projects</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {supervisors.map(u => {
                const assignedIds = u.field_supervisor_profile?.assigned_allocation_ids || [];
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }}>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.phone || '-'}</td>
                    <td>{u.field_supervisor_profile?.employee_id || '-'}</td>
                    <td style={{ fontSize: 12, maxWidth: 200 }}>
                      {assignedIds.length === 0 ? <span style={{ color: '#999' }}>None assigned</span>
                        : assignedIds.map(id => getProjectName(id)).join(', ')}
                    </td>
                    <td><StatusBadge status={u.status} /></td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm" style={{ background: '#1976d2', color: '#fff' }}
                        onClick={() => openEditAssign(u)}>Projects</button>
                      <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }}
                        onClick={() => handleRemove(u.id)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Main AgencyDashboard component
   ────────────────────────────────────────────── */
export default function AgencyDashboard() {
  return (
    <DashboardLayout navItems={navItems} title="Implementing Agency Dashboard">
      <Routes>
        <Route path="" element={<Overview />} />
        <Route path="opportunities" element={<Opportunities />} />
        <Route path="applications" element={<Applications />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="reports" element={<Reports />} />
        <Route path="adoption-sites" element={<ManagedAdoptionSites />} />
        <Route path="adoption-reports" element={<AdoptionMaintenanceReports />} />
        <Route path="ideas" element={<AgencyIdeas />} />
        <Route path="blog" element={<MyBlogPosts api={agencyAPI} />} />
        <Route path="monitoring-progress" element={<AgencyMonitoringProgress />} />
        <Route path="species-report" element={<SpeciesReport />} />
        <Route path="zones" element={<ZoneManagement />} />
        <Route path="group-monitoring" element={<GroupMonitoring />} />
        <Route path="field-supervisors" element={<FieldSupervisors />} />
      </Routes>
    </DashboardLayout>
  );
}
